import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  Breadcrumb,
  Carousel,
  Image,
} from "react-bootstrap";
import { useParams, Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import colors from "../../constants/colors";
import { formatPrice } from "../../utils/formatPrice";
import { FaCartPlus, FaMinus, FaPlus, FaImage, FaPen } from "react-icons/fa";
import SEO from "../../components/SEO";

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [metadata, setMetadata] = useState(null);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/product/${id}`);
        const metadataResponse = await axiosInstance.get(`/seo/metadata/product/${id}`);
        setProduct(response);
        setMetadata(metadataResponse);
        
        if (response.hasVariants && response.variants && response.variants.length > 0) {
          setSelectedVariant(response.variants[0]);
        }
        
        setError("");
      } catch (err) {
        console.error("Error fetching product details:", err);
        setError(
          "Erro ao carregar detalhes do produto. Por favor tente novamente mais tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      if (product.hasVariants && selectedVariant) {
        addToCart({
          ...product,
          price: selectedVariant.price,
          stock: selectedVariant.stock,
          variant: selectedVariant
        }, quantity);
      } else {
        addToCart(product, quantity);
      }
    }
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate("/carrinho");
  };

  const handleCarouselSelect = (selectedIndex) => {
    setCarouselIndex(selectedIndex);
  };

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    setQuantity(1); // Reset quantity when changing variant
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status" style={{ color: colors.secondary }}
        />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container className="my-5">
        <Alert variant="warning">Produto não encontrado.</Alert>
      </Container>
    );
  }

  const currentPrice = product.hasVariants && selectedVariant 
    ? selectedVariant.price 
    : product.price;

  const currentStock = product.hasVariants && selectedVariant 
    ? selectedVariant.stock 
    : product.stock;

  return (
    <>
      <SEO
        title={product.name}
        description={product.description}
        image={product.images?.[0]?.src}
        structuredData={metadata}
      />
      <Container className="my-5">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/produtos" }}>
            Produtos
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{product.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Row>
          <Col lg={6} className="mb-4">
            {product.images && product.images.length > 0 ? (
              <>
                <Carousel
                  activeIndex={carouselIndex}
                  onSelect={handleCarouselSelect}
                  interval={null}
                  className="mb-3"
                >
                  {product.images.map((image, idx) => (
                    <Carousel.Item key={idx}>
                      <div
                        className="d-flex justify-content-center align-items-center bg-light"
                        style={{ height: "400px" }}
                      >
                        <img
                          className="d-block"
                          src={image.src}
                          alt={`${product.name} - ${idx + 1}`}
                          style={{
                            height: "100%",
                            width: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>

                {product.images.length > 1 && (
                  <Row xs={6} className="g-2">
                    {product.images.map((image, idx) => (
                      <Col key={idx}>
                        <Image
                          src={image.src}
                          alt={`Thumbnail ${idx}`}
                          thumbnail
                          className={`cursor-pointer ${
                            carouselIndex === idx ? "border-primary" : ""
                          }`}
                          onClick={() => setCarouselIndex(idx)}
                          style={{ height: "60px", objectFit: "cover" }}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center bg-light"
                style={{ height: "400px", width: "100%" }}
              >
                <FaImage className="text-muted" style={{ fontSize: "5rem" }} />
              </div>
            )}
          </Col>

          <Col lg={6}>
            <h1 className="mb-3">{product.name}</h1>

            {product.isDigital && (
              <div className="mb-3">
                <span className="badge bg-info fs-6">Produto Digital</span>
              </div>
            )}

            <h2 className="mb-4">{formatPrice(currentPrice)}€</h2>

            <div
              className="mb-4"
              style={{
                color: colors.textGray,
                fontSize: "1.1rem",
                lineHeight: "1.5",
                overflowWrap: "break-word"
              }}
            >
              <p className="mb-2">{product.description}</p>
            </div>

            {product.hasVariants && product.variants && (
              <Form.Group className="mb-4">
                <Form.Label>Variante</Form.Label>
                <Form.Select
                  value={selectedVariant?.id || ''}
                  onChange={(e) => {
                    const variant = product.variants.find(v => v.id === parseInt(e.target.value));
                    handleVariantChange(variant);
                  }}
                >
                  {product.variants.map(variant => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name} - {formatPrice(variant.price)}€
                      {variant.stock <= 0 ? ' (Fora de stock)' : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {(currentStock > 0 || product.isDigital) && (
              <>
                <Form.Group className="mb-4">
                  <Form.Label>Quantidade</Form.Label>
                  <div className="d-flex align-items-center">
                    <Button
                      variant="outline-secondary"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >
                      <FaMinus />
                    </Button>

                    <Form.Control
                      type="number"
                      min="1"
                      max={currentStock}
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(
                          Math.min(
                            currentStock,
                            Math.max(1, parseInt(e.target.value) || 1)
                          )
                        )
                      }
                      className="text-center mx-2"
                      style={{ width: "70px" }}
                    />

                    <Button
                      variant="outline-secondary"
                      onClick={() =>
                        setQuantity(Math.min(currentStock, quantity + 1))
                      }
                    >
                      <FaPlus />
                    </Button>
                  </div>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    onClick={handleAddToCart}
                    size="lg"
                    style={{
                      backgroundColor: colors.secondary,
                      borderColor: colors.secondary,
                      color: colors.light,
                    }}
                  >
                    <FaCartPlus className="me-2" />
                    Adicionar ao Carrinho
                  </Button>

                  <Button
                    onClick={handleBuyNow}
                    size="lg"
                    style={{
                      backgroundColor: colors.light,
                      borderColor: colors.success,
                      color: colors.success,
                    }}
                  >
                    Comprar Agora
                  </Button>
                </div>
              </>
            )}

            {user && user.isAdmin && (
              <div className="mt-4">
                <Button
                  as={Link}
                  to={`/admin-dashboard/edit-product/${product.id}`}
                  style={{
                    backgroundColor: colors.dark,
                    borderColor: colors.dark,
                    color: colors.primary,
                  }}
                >
                  <FaPen className="me-2" />
                  Editar Produto
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ProductDetailPage;
